import { Organization } from "@/types/organization"

import { env } from "@/lib/env"
import { present } from "@/lib/utils/presence"
import {
  AmplitudeEvent,
  AmplitudeEventAndProperties,
  AmplitudeEventWithProps,
} from "./amplitude-types"
import {
  AmplitudeInitChatProps,
  AmplitudeInitProps,
  AmplitudeInitRateLimitProps,
  AmplitudeInitRegisterGateProps,
  AmplitudeInitUtmProps,
} from "./amplitude-utils"

// Declare this for TypeScript.  Only do it here since we are only using here.
/* eslint-disable @typescript-eslint/no-explicit-any */
type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[]
  gtag: (...args: any[]) => void // Declare gtag
}
declare const window: WindowWithDataLayer

// Too noisy - suppressed
const SuppressEventList: (AmplitudeEvent | AmplitudeEventWithProps)[] = [
  "LocationSelect",
  "DialogDestinationClose",
]

export const AllowEventList: (AmplitudeEvent | AmplitudeEventWithProps)[] = [
  "ViewHomePage",
  "ViewAppHomePage",
  "ViewChatPage",
  // "ViewChatGroupPage", - removed for now
  "ChatCreate",
  "ChatUserMessageAdd",
  "ViewPlaceDetailSlideOut",
  "PlaceFavoriteAdd",
  "ViewTripSlideOut",
  "ItineraryAdd",
  "ViewPlaceDetailAllPhotos",
  "PlaceBookDirect",
  "TripShared",
  "ClickPlaceLink",
  "ClickExternalLink",
  "ClickChatPill",
  "ClickAskMindtrip",
  "ViewExplorePage",
  "ViewGuidePage",
  "ViewSavedPage",
  "GetDirectionsTapped",
  // Main trips pages
  "ViewTripsPage",
  "ViewTripHub",
  "TripCreate",
  // Embedded event
  "EmbeddedWidgetOpen",
  "EmbeddedWidgetClose",
  // Optout and OptIn
  "UserOptOut",
  "UserOptIn",
  // Server events
  "ChatBotMessageAdd",
  "FlightAgentCall",
  "ItineraryGenerate",
  "TripUserCreated",
  "UserRegister",
]

export const AllowUserPropertiesList: string[] = Array.from(
  new Set([
    "uuid",
    "provider",
    // "handle",
    "status",
    "orgid",
    "chat",
    "trip",
    // "has_thatch_data",
    // "is_from_thatch",
    // "is_thatch_creator",
  ])
)

export const AllowEventPropertiesList: string[] = Array.from(
  new Set([
    // For ViewChatPage -> has no event properties
    // For ChatCreate
    "chat",
    // For ChatUserMessageAdd
    "chatType",
    "prompt",
    "voice_chat",
    // For ViewPlaceDetailSlideOut
    "location",
    // "urlPath", -> this just points to /chat/xxxxs
    // For PlaceFavoriteAdd
    "location",
    // For ViewTripSlideOut -> has no event properties
    // For ItineraryAdd
    "event",
    // For ViewPlaceDetailAllPhotos
    "location",
    // For PlaceBookDirect
    "location",
    "url",
    // For TripShared
    "emails", // Should be count of emails
    // For ViewExplorePage (sends "type", the ExploreType)
    "type",
    // For ViewGuidePage
    "inspiration", // Should be rename to "guide"
    // OptOut
    "optOut",
    // For ViewTripHub, TripCreate
    "trip",
    // For ClickChatPill
    "title",
    // For GetDirectionsTapped
    "place",
    // For EmbeddedWidgetOpen
    "user_message",
  ])
)

export const InitUserProps = "InitUserProps"
export const InitChatProps = "InitChatProps"

let organization: Organization
let optOut = false

export const initGTMOrganization = (newOrganization: Organization) => {
  organization = newOrganization
}

// our own GTM container; unset in EU
export const gtmEnabled = () => {
  return !optOut && !!env("NEXT_PUBLIC_GTM_ACCOUNTS")
}

// our dataLayer, plus forwarding to the partner's page when embedded
const trackingEnabled = () => {
  return gtmEnabled() || (!optOut && !!organization)
}

export const sendGTMUserProperties = (
  properties:
    | AmplitudeInitProps
    | AmplitudeInitChatProps
    | AmplitudeInitRateLimitProps
    | AmplitudeInitUtmProps
    | AmplitudeInitRegisterGateProps
) => {
  if (trackingEnabled()) {
    // Don't expose the properties.type = rate limit, utm, or registerGate
    if (properties.type === "initProps" || properties.type === "chatProps") {
      const initPropsName =
        properties.type === "initProps" ? InitUserProps : InitChatProps
      window.dataLayer = window.dataLayer || []
      const data = {
        event: initPropsName,
        properties: {
          ...(present(properties)
            ? Object.fromEntries(
                Object.entries(properties).filter(([key]) =>
                  AllowUserPropertiesList.includes(key)
                )
              )
            : []),
        },
      }
      window.dataLayer.push(data)

      if (organization) {
        // Post the message to the parent window for cross domain
        postMessage(data, organization, "sendGTMUserProperties")
      }
    }
  }
}

export const clearGTMChatProperties = () => {
  if (trackingEnabled()) {
    window.dataLayer = window.dataLayer || []
    const data = {
      event: "ClearChatProperties",
      properties: { chat: undefined, trip: undefined },
    }
    window.dataLayer.push(data)

    if (organization) {
      // Post the message to the parent window for cross domain
      postMessage(data, organization, "clearGTMChatProperties")
    }
  }
}

export const clearEventProperties = () => {
  if (trackingEnabled()) {
    if (window.dataLayer) {
      for (let i = window.dataLayer.length - 1; i >= 0; i--) {
        const dataLayerValue = window.dataLayer[i]
        if (dataLayerValue?.properties?.eventProperties) {
          // Found one, so make sure to clear all these
          const clearedEventProperties = Object.fromEntries(
            Object.keys(dataLayerValue?.properties?.eventProperties).map(
              (key) => [key, undefined]
            )
          )
          return clearedEventProperties
        }
      }
    }
  }
  return {}
}

// Probably should not do this
//
// export const clearAllDataLayer = () => {
//   if (window.dataLayer) {
//     window.dataLayer.length = 0 // Resets the array
//   }
// }

export const optOutGTMTracking = (newOptOut: boolean) => {
  optOut = newOptOut

  // These below do not work.  Need to spend more time figuring out why.

  // if (gtmEnabled()) {
  //   window.dataLayer = window.dataLayer || [];
  //   if (newOptOut) {
  //     window.dataLayer.push({
  //       event: "consent_optout",
  //       "gtm.consent": {
  //         ad_storage: "denied",
  //         analytics_storage: "denied"
  //       }
  //     })
  //   }
  //   else {
  //     window.dataLayer.push({
  //       event: "consent_optout",
  //       "gtm.consent": {
  //         ad_storage: "granted",
  //         analytics_storage: "granted"
  //       }
  //     })
  //   }
  // }

  // Ran into window.gtag is not defined
  //
  // if (gtmEnabled() && window.gtag) {
  //   if (newOptOut) {
  //     // Push that user optout
  //     const data = {
  //       event: "UserOptOut",
  //       properties: {
  //         optOut: newOptOut,
  //       },
  //     }
  //     window.dataLayer.push(data)

  //     // Finally optout
  //     window.gtag("consent", "default", {
  //       ad_storage: "denied",
  //       analytics_storage: "denied",
  //     })
  //   } else {
  //     // First set optIn
  //     window.gtag("consent", "default", {
  //       ad_storage: "denied",
  //       analytics_storage: "denied",
  //     })
  //     // Push that user has optIn
  //     const data = {
  //       event: "UserOptIn",
  //       properties: {
  //         optOut: newOptOut,
  //       },
  //     }
  //     window.dataLayer.push(data)
  //   }
  // }
}

export const resetGTMProperties = () => {
  if (trackingEnabled() && window.dataLayer) {
    const data = {
      event: "ResetDataLayer",
      properties: undefined,
    }
    window.dataLayer.push(data)

    if (organization) {
      // Post the message to the parent window for cross domain
      postMessage(data, organization, "resetGTMProperties")
    }
  }
}

export const sendGTMEvent = (
  eventArgs: AmplitudeEvent | AmplitudeEventAndProperties
): boolean => {
  const event = typeof eventArgs === "string" ? eventArgs : eventArgs.name

  // GTM enabled and the event is white listed
  if (trackingEnabled() && AllowEventList.includes(event)) {
    const properties =
      typeof eventArgs === "string" ? undefined : eventArgs.properties

    if (!SuppressEventList.includes(event)) {
      window.dataLayer = window.dataLayer || []

      // Clear the dataLayer (if any)
      // if (ClearDataLayerEventList.includes(event)) {
      //   clearAllDataLayer()
      // }

      // Clear the old event properties first
      const oldClearedEventProperties = clearEventProperties()

      // First, only allow certain properties to be sent out
      const newEventProperites = present(properties)
        ? Object.fromEntries(
            Object.entries(properties).filter(
              ([key, value]) =>
                AllowEventPropertiesList.includes(key) && value !== undefined
            )
          )
        : {}

      // Now transform the properties
      if ("inspiration" in newEventProperites) {
        newEventProperites["guide"] = newEventProperites["inspiration"] // Change from "inspiration" -> "guide"
        delete newEventProperites["inspiration"] // Remove the old key
      }

      if ("emails" in newEventProperites) {
        newEventProperites["emailsCount"] = newEventProperites["emails"].length // Don't send the email, instead send just the count
        delete newEventProperites["emails"] // Remove the old key
      }

      // Now push the new event and its properties
      const data = {
        event: `${event}`,
        properties: {
          eventProperties: {
            ...oldClearedEventProperties,
            ...newEventProperites,
          },
        },
      }
      window.dataLayer.push(data)

      if (organization) {
        // Post the message to the parent window for cross domain
        postMessage(data, organization, "sendGTMEvent")
      }

      return true
    } else {
      console.debug("GTM event suppressed for: ", event, properties)
    }
  }
  return false // Indicate event not fired
}

export const postMessage = (data: any, org: Organization, type: string) => {
  // collapse urls to unique set of origins to pass to postMessage
  const origins = new Set<string>()
  const addOrigin = (url?: string) => {
    try {
      if (url && url.startsWith("http") && url.indexOf("*") === -1) {
        origins.add(new URL(url).origin)
      }
    } catch {
      // ignore pasre error
    }
  }

  addOrigin(org.website_url)
  // in case org is testing on an alternate domain, post to each fully qualified & non-wildcard url
  org.properties?.alternate_website_urls?.forEach((url) => {
    addOrigin(url)
  })

  origins.forEach((origin) => {
    window.parent.postMessage(data, origin)
  })

  console.debug(`Analytics: ${type}: postMessage:`, [...origins], data)
}
